import { useState } from 'react';
import { agencyInfo } from '../../mocks/dashboard';
import StaffSection from './components/StaffSection';
import BillingSection from './components/BillingSection';

type Section = 'profile' | 'branding' | 'finance' | 'notifications' | 'staff' | 'billing';

const PRESET_COLORS = [
  '#0D9488', '#059669', '#16A34A', '#D97706', '#B45309',
  '#DC2626', '#E11D48', '#DB2777', '#0891B2', '#0F766E',
  '#374151', '#1F2937',
];

const CURRENCIES = ['AED', 'USD', 'EUR', 'GBP', 'SAR', 'QAR', 'KWD', 'BHD', 'OMR', 'EGP', 'INR', 'SGD'];

const TIMEZONES = [
  'Asia/Dubai', 'Asia/Riyadh', 'Asia/Kuwait', 'Asia/Qatar', 'Asia/Bahrain', 'Asia/Muscat',
  'Africa/Cairo', 'Africa/Casablanca', 'Africa/Johannesburg',
  'Europe/London', 'Europe/Paris', 'Europe/Berlin', 'Europe/Rome', 'Europe/Istanbul',
  'America/New_York', 'America/Los_Angeles', 'America/Chicago', 'America/Toronto',
  'Asia/Singapore', 'Asia/Tokyo', 'Asia/Bangkok', 'Asia/Kolkata', 'Asia/Dubai',
  'Australia/Sydney', 'Pacific/Auckland', 'UTC',
];

interface ToggleSetting { id: string; label: string; desc: string; on: boolean }

const initNotifSettings: ToggleSetting[] = [
  { id: 'n1', label: 'New booking received',       desc: 'Alert when a new booking is created in the system', on: true  },
  { id: 'n2', label: 'New lead assigned',           desc: 'Alert when a lead is assigned to you',            on: true  },
  { id: 'n3', label: 'Payment received',            desc: 'Notify when a customer payment is recorded',      on: true  },
  { id: 'n4', label: 'Invoice overdue',             desc: 'Alert when an invoice passes its due date',       on: true  },
  { id: 'n5', label: 'Task due reminder',           desc: 'Remind 24 hours before a task is due',            on: true  },
  { id: 'n6', label: 'Booking departure reminder',  desc: 'Alert 72 hours before customer departure date',   on: true  },
  { id: 'n7', label: 'New staff invitation accepted', desc: 'When invited staff completes registration',     on: false },
  { id: 'n8', label: 'Weekly summary report',       desc: 'Email digest every Monday with KPIs & insights',  on: true  },
  { id: 'n9', label: 'System maintenance alerts',   desc: 'Notify about scheduled downtime and updates',     on: false },
];

export default function SettingsPage() {
  const brand = agencyInfo.brandColor;
  const [activeSection, setActiveSection] = useState<Section>('profile');
  const [saved, setSaved] = useState<string | null>(null);

  // Agency Profile state
  const [agencyName, setAgencyName] = useState('SkyWave Travel');
  const [agencyTagline, setAgencyTagline] = useState('Your Premium Travel Partner in Dubai');
  const [agencyEmail, setAgencyEmail] = useState('info@skywavetravel.ae');
  const [agencyPhone, setAgencyPhone] = useState('+971 4 123 4567');
  const [agencyWebsite, setAgencyWebsite] = useState('www.skywavetravel.ae');
  const [agencyAddress, setAgencyAddress] = useState('Suite 802, Emaar Business Park, Sheikh Zayed Road, Dubai, UAE');
  const [iataNumber, setIataNumber] = useState('1A-UAE-2024-0088');
  const [licenseNo, setLicenseNo] = useState('DET-2024-TRV-4421');
  const [founded, setFounded] = useState('2018');
  const [agencyTimezone, setAgencyTimezone] = useState('Asia/Dubai');
  const [agencyPreferredCurrency, setAgencyPreferredCurrency] = useState('AED');

  // Branding state
  const [brandColor, setBrandColor] = useState(brand);
  const [hexInput, setHexInput] = useState(brand);
  const [logoInitials, setLogoInitials] = useState('SW');

  // Finance state
  const [currency, setCurrency] = useState('AED');
  const [vatRate, setVatRate] = useState('5');
  const [invPrefix, setInvPrefix] = useState('INV-2026-');
  const [payTerms, setPayTerms] = useState('7');
  const [bankName, setBankName] = useState('Emirates NBD');
  const [accountName, setAccountName] = useState('SkyWave Travel LLC');
  const [accountNo, setAccountNo] = useState('1234567890');
  const [iban, setIban] = useState('AE070331234567890123456');
  const [swift, setSwift] = useState('EBILAEAD');
  const [payMethods, setPayMethods] = useState({ bankTransfer: true, creditCard: true, cash: true, cheque: false });

  // Notifications
  const [notifs, setNotifs] = useState<ToggleSetting[]>(initNotifSettings);

  const toggleNotif = (id: string) => setNotifs((prev) => prev.map((n) => n.id === id ? { ...n, on: !n.on } : n));

  const handleSave = (section: string) => {
    setSaved(section);
    setTimeout(() => setSaved(null), 2500);
  };

  const handleColorChange = (c: string) => {
    setBrandColor(c);
    setHexInput(c);
  };

  const handleHexInput = (val: string) => {
    setHexInput(val);
    if (/^#[0-9A-Fa-f]{6}$/.test(val)) setBrandColor(val);
  };

  const nav: { id: Section; label: string; icon: string }[] = [
    { id: 'profile',       label: 'Agency Profile',  icon: 'ri-building-4-line' },
    { id: 'branding',      label: 'Branding',         icon: 'ri-palette-line' },
    { id: 'finance',       label: 'Finance & Tax',    icon: 'ri-money-dollar-circle-line' },
    { id: 'notifications', label: 'Notifications',    icon: 'ri-notification-3-line' },
    { id: 'staff',         label: 'Staff Management', icon: 'ri-team-line' },
    { id: 'billing',       label: 'Billing & Plan',   icon: 'ri-bank-card-line' },
  ];

  const Toggle = ({ on, onToggle }: { on: boolean; onToggle: () => void }) => (
    <button
      onClick={onToggle}
      className={`w-11 h-6 rounded-full relative transition-colors cursor-pointer flex-shrink-0 ${on ? '' : 'bg-gray-200'}`}
      style={on ? { backgroundColor: brand } : {}}
    >
      <div className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-transform ${on ? 'translate-x-5' : 'translate-x-0.5'}`} />
    </button>
  );

  return (
    <div className="min-h-screen bg-gray-50/50 p-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Settings</h1>
        <p className="text-sm text-gray-500 mt-0.5">Configure your agency, branding, team, and billing preferences</p>
      </div>

      <div className="flex gap-6 items-start">
        {/* Left Nav */}
        <div className="w-56 flex-shrink-0">
          <nav className="bg-white border border-gray-100 rounded-2xl p-2 sticky top-8">
            {nav.map((item) => {
              const active = activeSection === item.id;
              return (
                <button
                  key={item.id}
                  onClick={() => setActiveSection(item.id)}
                  className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-sm font-medium transition-all cursor-pointer text-left ${
                    active ? 'text-white' : 'text-gray-600 hover:bg-gray-50'
                  }`}
                  style={active ? { backgroundColor: brand } : {}}
                >
                  <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                    <i className={`${item.icon} text-base`} />
                  </div>
                  {item.label}
                </button>
              );
            })}
          </nav>
        </div>

        {/* Main Content */}
        <div className="flex-1 min-w-0">

          {/* ── Agency Profile ── */}
          {activeSection === 'profile' && (
            <div className="bg-white border border-gray-100 rounded-2xl p-6 space-y-6">
              <div>
                <h2 className="font-bold text-gray-900 text-base">Agency Profile</h2>
                <p className="text-sm text-gray-400 mt-0.5">Your agency&apos;s public-facing information</p>
              </div>

              {/* Logo preview */}
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-2xl flex items-center justify-center text-white font-bold text-xl flex-shrink-0"
                  style={{ backgroundColor: brandColor }}>
                  {logoInitials}
                </div>
                <div>
                  <p className="font-semibold text-gray-800">{agencyName || 'Agency Name'}</p>
                  <p className="text-sm text-gray-400">{agencyTagline || 'Tagline'}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="block text-xs font-medium text-gray-600 mb-1.5">Agency Name</label>
                  <input type="text" value={agencyName} onChange={(e) => setAgencyName(e.target.value)}
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none" />
                </div>
                <div className="col-span-2">
                  <label className="block text-xs font-medium text-gray-600 mb-1.5">Tagline / Slogan</label>
                  <input type="text" value={agencyTagline} onChange={(e) => setAgencyTagline(e.target.value)}
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1.5">Business Email</label>
                  <input type="email" value={agencyEmail} onChange={(e) => setAgencyEmail(e.target.value)}
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1.5">Phone Number</label>
                  <input type="text" value={agencyPhone} onChange={(e) => setAgencyPhone(e.target.value)}
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1.5">Website</label>
                  <input type="text" value={agencyWebsite} onChange={(e) => setAgencyWebsite(e.target.value)}
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1.5">Founded Year</label>
                  <input type="text" value={founded} onChange={(e) => setFounded(e.target.value)}
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1.5">Timezone</label>
                  <select value={agencyTimezone} onChange={(e) => setAgencyTimezone(e.target.value)}
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none cursor-pointer">
                    {TIMEZONES.map((tz) => <option key={tz} value={tz}>{tz.replace('_', ' ')}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1.5">Preferred Currency</label>
                  <select value={agencyPreferredCurrency} onChange={(e) => setAgencyPreferredCurrency(e.target.value)}
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none cursor-pointer">
                    {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div className="col-span-2">
                  <label className="block text-xs font-medium text-gray-600 mb-1.5">Office Address</label>
                  <input type="text" value={agencyAddress} onChange={(e) => setAgencyAddress(e.target.value)}
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1.5">IATA Number</label>
                  <input type="text" value={iataNumber} onChange={(e) => setIataNumber(e.target.value)}
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1.5">Trade License No.</label>
                  <input type="text" value={licenseNo} onChange={(e) => setLicenseNo(e.target.value)}
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none" />
                </div>
              </div>
              <div className="flex items-center justify-between pt-4 border-t border-gray-50">
                {saved === 'profile' && (
                  <span className="flex items-center gap-1.5 text-emerald-600 text-sm">
                    <i className="ri-checkbox-circle-line" /> Profile saved!
                  </span>
                )}
                <div className="ml-auto">
                  <button onClick={() => handleSave('profile')}
                    className="px-6 py-2.5 rounded-xl text-white text-sm font-semibold cursor-pointer"
                    style={{ backgroundColor: brand }}>
                    Save Profile
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* ── Branding ── */}
          {activeSection === 'branding' && (
            <div className="bg-white border border-gray-100 rounded-2xl p-6 space-y-6">
              <div>
                <h2 className="font-bold text-gray-900 text-base">Branding</h2>
                <p className="text-sm text-gray-400 mt-0.5">Customize your brand colors and appearance</p>
              </div>

              {/* Live Preview */}
              <div className="border border-gray-100 rounded-2xl p-5 bg-gray-50/50">
                <p className="text-xs text-gray-400 uppercase font-medium tracking-wider mb-4">Live Preview</p>
                <div className="flex items-center gap-4">
                  {/* Mini sidebar preview */}
                  <div className="w-36 bg-white border border-gray-100 rounded-xl p-3 space-y-1.5">
                    <div className="flex items-center gap-2 pb-2 border-b border-gray-50 mb-2">
                      <div className="w-7 h-7 rounded-lg flex items-center justify-center text-white text-xs font-bold" style={{ backgroundColor: brandColor }}>
                        {logoInitials}
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-gray-700 truncate" style={{ maxWidth: '70px' }}>{agencyName || 'Agency'}</p>
                      </div>
                    </div>
                    {['Dashboard', 'Bookings', 'CRM', 'Settings'].map((item, i) => (
                      <div key={item} className={`flex items-center gap-1.5 px-2 py-1.5 rounded-lg text-xs ${i === 0 ? 'text-white font-medium' : 'text-gray-500'}`}
                        style={i === 0 ? { backgroundColor: brandColor } : {}}>
                        <div className="w-3 h-3 rounded bg-current opacity-60" />
                        {item}
                      </div>
                    ))}
                  </div>
                  {/* Button preview */}
                  <div className="flex-1 space-y-3">
                    <button className="px-5 py-2.5 rounded-xl text-white text-sm font-semibold" style={{ backgroundColor: brandColor }}>
                      Primary Button
                    </button>
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-lg flex items-center justify-center text-white text-sm font-bold" style={{ backgroundColor: brandColor }}>
                        {logoInitials[0] || 'A'}
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-gray-700">Brand Badge</p>
                        <p className="text-xs font-medium" style={{ color: brandColor }}>Active link color</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-20 h-2 rounded-full" style={{ backgroundColor: brandColor }} />
                      <span className="text-xs text-gray-400">Progress bar</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Color Picker */}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-3">Brand Color</label>
                <div className="flex flex-wrap gap-2 mb-4">
                  {PRESET_COLORS.map((c) => (
                    <button
                      key={c}
                      onClick={() => handleColorChange(c)}
                      className={`w-8 h-8 rounded-lg cursor-pointer transition-all ${brandColor === c ? 'ring-2 ring-offset-2 scale-110' : 'hover:scale-105'}`}
                      style={{ backgroundColor: c, ringColor: c }}
                    />
                  ))}
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl flex-shrink-0 border border-gray-200" style={{ backgroundColor: brandColor }} />
                  <div className="flex-1">
                    <label className="block text-xs text-gray-400 mb-1">Custom HEX</label>
                    <input
                      type="text"
                      value={hexInput}
                      onChange={(e) => handleHexInput(e.target.value)}
                      placeholder="#0D9488"
                      className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm font-mono focus:outline-none"
                    />
                  </div>
                </div>
              </div>

              {/* Logo Initials */}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1.5">Logo Initials (shown in sidebar)</label>
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center text-white font-bold text-lg flex-shrink-0"
                    style={{ backgroundColor: brandColor }}>
                    {logoInitials || 'SW'}
                  </div>
                  <input type="text" value={logoInitials} onChange={(e) => setLogoInitials(e.target.value.toUpperCase().slice(0, 3))}
                    maxLength={3} placeholder="SW"
                    className="w-24 px-3 py-2.5 border border-gray-200 rounded-xl text-sm font-bold uppercase text-center focus:outline-none" />
                  <p className="text-xs text-gray-400">2–3 characters, uppercase</p>
                </div>
              </div>

              <div className="flex items-center justify-between pt-4 border-t border-gray-50">
                {saved === 'branding' && (
                  <span className="flex items-center gap-1.5 text-emerald-600 text-sm">
                    <i className="ri-checkbox-circle-line" /> Branding saved!
                  </span>
                )}
                <div className="ml-auto">
                  <button onClick={() => handleSave('branding')}
                    className="px-6 py-2.5 rounded-xl text-white text-sm font-semibold cursor-pointer"
                    style={{ backgroundColor: brandColor }}>
                    Save Branding
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* ── Finance & Tax ── */}
          {activeSection === 'finance' && (
            <div className="bg-white border border-gray-100 rounded-2xl p-6 space-y-6">
              <div>
                <h2 className="font-bold text-gray-900 text-base">Finance & Tax</h2>
                <p className="text-sm text-gray-400 mt-0.5">Currency, VAT, invoicing rules, and bank details</p>
              </div>

              {/* General Finance */}
              <div>
                <p className="text-xs text-gray-400 uppercase font-medium tracking-wider mb-3">General</p>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1.5">Default Currency</label>
                    <select value={currency} onChange={(e) => setCurrency(e.target.value)}
                      className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none cursor-pointer">
                      {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1.5">VAT Rate (%)</label>
                    <input type="number" min="0" max="100" step="0.1" value={vatRate}
                      onChange={(e) => setVatRate(e.target.value)}
                      className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1.5">Invoice Number Prefix</label>
                    <input type="text" value={invPrefix} onChange={(e) => setInvPrefix(e.target.value)}
                      className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none font-mono" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1.5">Default Payment Terms (days)</label>
                    <input type="number" min="1" value={payTerms} onChange={(e) => setPayTerms(e.target.value)}
                      className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none" />
                  </div>
                </div>
              </div>

              {/* Payment Methods */}
              <div>
                <p className="text-xs text-gray-400 uppercase font-medium tracking-wider mb-3">Accepted Payment Methods</p>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { key: 'bankTransfer', label: 'Bank Transfer', icon: 'ri-bank-line' },
                    { key: 'creditCard',   label: 'Credit / Debit Card', icon: 'ri-bank-card-line' },
                    { key: 'cash',         label: 'Cash',           icon: 'ri-money-dollar-circle-line' },
                    { key: 'cheque',       label: 'Cheque',         icon: 'ri-draft-line' },
                  ].map(({ key, label, icon }) => {
                    const on = payMethods[key as keyof typeof payMethods];
                    return (
                      <div key={key} className={`flex items-center gap-3 p-4 border rounded-xl cursor-pointer transition-all ${on ? 'border-current' : 'border-gray-100'}`}
                        style={on ? { borderColor: brand, backgroundColor: `${brand}06` } : {}}
                        onClick={() => setPayMethods((p) => ({ ...p, [key]: !on }))}>
                        <div className="w-8 h-8 flex items-center justify-center rounded-lg" style={{ backgroundColor: on ? `${brand}15` : '#F3F4F6' }}>
                          <i className={`${icon} text-sm`} style={{ color: on ? brand : '#9CA3AF' }} />
                        </div>
                        <span className={`text-sm font-medium ${on ? 'text-gray-800' : 'text-gray-400'}`}>{label}</span>
                        <div className="ml-auto">
                          <div className={`w-9 h-5 rounded-full relative transition-colors ${on ? '' : 'bg-gray-200'}`} style={on ? { backgroundColor: brand } : {}}>
                            <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform ${on ? 'translate-x-4' : 'translate-x-0.5'}`} />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Bank Details */}
              <div>
                <p className="text-xs text-gray-400 uppercase font-medium tracking-wider mb-3">Bank Account Details</p>
                <p className="text-xs text-gray-400 mb-4">Shown on invoices and payment receipts</p>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1.5">Bank Name</label>
                    <input type="text" value={bankName} onChange={(e) => setBankName(e.target.value)}
                      className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1.5">Account Name</label>
                    <input type="text" value={accountName} onChange={(e) => setAccountName(e.target.value)}
                      className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1.5">Account Number</label>
                    <input type="text" value={accountNo} onChange={(e) => setAccountNo(e.target.value)}
                      className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm font-mono focus:outline-none" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1.5">SWIFT / BIC Code</label>
                    <input type="text" value={swift} onChange={(e) => setSwift(e.target.value)}
                      className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm font-mono focus:outline-none" />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-xs font-medium text-gray-600 mb-1.5">IBAN</label>
                    <input type="text" value={iban} onChange={(e) => setIban(e.target.value)}
                      className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm font-mono focus:outline-none" />
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between pt-4 border-t border-gray-50">
                {saved === 'finance' && (
                  <span className="flex items-center gap-1.5 text-emerald-600 text-sm">
                    <i className="ri-checkbox-circle-line" /> Finance settings saved!
                  </span>
                )}
                <div className="ml-auto">
                  <button onClick={() => handleSave('finance')}
                    className="px-6 py-2.5 rounded-xl text-white text-sm font-semibold cursor-pointer"
                    style={{ backgroundColor: brand }}>
                    Save Finance Settings
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* ── Notifications ── */}
          {activeSection === 'notifications' && (
            <div className="bg-white border border-gray-100 rounded-2xl p-6 space-y-6">
              <div>
                <h2 className="font-bold text-gray-900 text-base">Notifications</h2>
                <p className="text-sm text-gray-400 mt-0.5">Control which events trigger email and system alerts</p>
              </div>

              <div className="space-y-2">
                {notifs.map((n) => (
                  <div key={n.id} className="flex items-center justify-between py-4 border-b border-gray-50 last:border-0">
                    <div className="flex-1 pr-6">
                      <p className="text-sm font-semibold text-gray-800">{n.label}</p>
                      <p className="text-xs text-gray-400 mt-0.5">{n.desc}</p>
                    </div>
                    <button
                      onClick={() => toggleNotif(n.id)}
                      className={`w-11 h-6 rounded-full relative transition-colors cursor-pointer flex-shrink-0 ${n.on ? '' : 'bg-gray-200'}`}
                      style={n.on ? { backgroundColor: brand } : {}}
                    >
                      <div className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-transform ${n.on ? 'translate-x-5' : 'translate-x-0.5'}`} />
                    </button>
                  </div>
                ))}
              </div>

              <div className="bg-gray-50 rounded-xl p-4 flex items-center justify-between">
                <div>
                  <p className="text-sm font-semibold text-gray-700">Notification delivery channel</p>
                  <p className="text-xs text-gray-400 mt-0.5">Currently sending to: {agencyEmail}</p>
                </div>
                <button className="text-xs font-medium px-3 py-1.5 rounded-lg border border-gray-200 bg-white text-gray-600 hover:bg-gray-50 cursor-pointer">
                  Change Email
                </button>
              </div>

              <div className="flex items-center justify-between pt-4 border-t border-gray-50">
                {saved === 'notifications' && (
                  <span className="flex items-center gap-1.5 text-emerald-600 text-sm">
                    <i className="ri-checkbox-circle-line" /> Notification preferences saved!
                  </span>
                )}
                <div className="ml-auto">
                  <button onClick={() => handleSave('notifications')}
                    className="px-6 py-2.5 rounded-xl text-white text-sm font-semibold cursor-pointer"
                    style={{ backgroundColor: brand }}>
                    Save Preferences
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* ── Staff Management ── */}
          {activeSection === 'staff' && (
            <div className="bg-white border border-gray-100 rounded-2xl p-6">
              <StaffSection />
            </div>
          )}

          {/* ── Billing ── */}
          {activeSection === 'billing' && (
            <div className="bg-white border border-gray-100 rounded-2xl p-6">
              <div className="mb-6">
                <h2 className="font-bold text-gray-900 text-base">Billing & Plan</h2>
                <p className="text-sm text-gray-400 mt-0.5">Manage your subscription, usage, and payment history</p>
              </div>
              <BillingSection />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
